// src/pages/RideSuccess.js
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";

const RideSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    from = "N/A",
    to = "N/A",
    date = "N/A",
    time = "N/A",
    pricePerSeat = "N/A",
    rideId = "N/A",
    bookingType = "manual",
    dropOffNotes = "",
    rideOptions = {},
    stops = [],
  } = location.state || {};

  const formatDate = (d) => {
    if (!d || d === "N/A") return d;
    const dateObj = new Date(d);
    return dateObj.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-100 flex justify-center items-center px-4 font-[Poppins]">
      <div className="w-full max-w-2xl bg-white/80 backdrop-blur-xl border border-white/40 shadow-2xl rounded-3xl p-8 animate-fadeIn">

        {/* HEADER */}
        <div className="flex flex-col items-center">
          <CheckCircle2
            className="text-green-600 drop-shadow-lg animate-bounce"
            size={70}
          />
          <h1 className="text-3xl font-extrabold mt-3 text-gray-900 tracking-tight">
            Ride Successfully Posted!
          </h1>
          <p className="text-gray-600 mt-1 text-sm">
            Your trip is now visible to riders in your region.
          </p>
        </div>

        {/* ROUTE CARD */}
        <div className="mt-8 bg-gradient-to-r from-blue-100 to-indigo-100 p-5 rounded-xl shadow-inner text-center">
          <h2 className="text-xl font-bold text-gray-900">
            {from} <span className="text-gray-500">→</span> {to}
          </h2>
        </div>

        {/* MAIN INFO GRID */}
        <div className="grid grid-cols-3 gap-4 mt-6 text-center">
          {[
            { label: "Date", value: formatDate(date) },
            { label: "Time", value: time },
            { label: "Price / Seat", value: `$${pricePerSeat}` },
          ].map((item, i) => (
            <div
              key={i}
              className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm"
            >
              <p className="text-xs text-gray-500">{item.label}</p>
              <p className="text-gray-800 font-semibold text-sm mt-1">
                {item.value}
              </p>
            </div>
          ))}
        </div>

        {/* DETAILS */}
        <div className="mt-8 space-y-3 text-sm text-gray-700">
          <div>
            <span className="font-semibold">Booking Type:</span>{" "}
            <span className="text-indigo-700 font-medium">
              {bookingType === "instant" ? "Instant Booking" : "Manual Review"}
            </span>
          </div>

          {dropOffNotes && (
            <div>
              <span className="font-semibold">Drop-Off Notes:</span>{" "}
              {dropOffNotes}
            </div>
          )}

          <div>
            <span className="font-semibold">Ride ID:</span>{" "}
            <span className="font-mono text-blue-600 text-[13px]">
              {rideId}
            </span>
          </div>
        </div>

        {/* STOPS */}
        {stops.length > 0 && (
          <div className="mt-8">
            <h3 className="text-md font-bold text-gray-900 mb-2">Stops</h3>
            <div className="space-y-3">
              {stops.map((s, idx) => (
                <div
                  key={idx}
                  className="bg-white border border-gray-200 p-4 rounded-xl shadow-sm flex justify-between"
                >
                  <div>
                    <p className="font-semibold text-gray-900">{s.name}</p>
                    <p className="text-xs text-gray-500">{s.pickup}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-600">{s.time}</p>
                    <p className="font-bold text-blue-600">${s.price}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* RIDE OPTIONS */}
        {rideOptions && Object.keys(rideOptions).length > 0 && (
          <div className="mt-8">
            <h3 className="text-md font-bold text-gray-900 mb-2">
              Ride Options
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Object.entries(rideOptions).map(([key, value]) => (
                <div
                  key={key}
                  className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700"
                >
                  <span className="font-semibold">
                    {key.replace(/([A-Z])/g, " $1").toUpperCase()}
                  </span>
                  :{" "}
                  {value === true
                    ? "Yes"
                    : value === false
                    ? "No"
                    : value || "N/A"}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA BUTTON */}
        <div className="mt-10">
          <button
            onClick={() => navigate("/ride/my-rides")}
            className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-bold py-3 rounded-xl shadow-lg hover:scale-[1.02] transition transform"
          >
            View My Rides
          </button>
        </div>

      </div>
    </div>
  );
};

export default RideSuccess;
